import { Link } from 'react-router-dom';
import { Gift, Wallet, Sparkles, ShieldCheck, RotateCcw, ArrowRight } from 'lucide-react';
import { bloomPricing, SIGNUP_BONUS_AMOUNT } from '../../lib/types';

const bloomDescriptions: Record<string, string> = {
  Remembering: 'Recall facts, terms, definitions',
  Understanding: 'Explain ideas and concepts',
  Applying: 'Apply knowledge to new situations',
  Analyzing: 'Break down information, compare',
  Evaluating: 'Justify decisions, judge value',
  Creating: 'Produce original work, design',
};

const orderedLevels = ['Remembering', 'Understanding', 'Applying', 'Analyzing', 'Evaluating', 'Creating'];

export default function PricingSection() {
  return (
    <section className="bg-slate-50 py-20 px-4 sm:px-6 lg:px-8" id="pricing">
      <div className="max-w-7xl mx-auto">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-semibold uppercase tracking-wider mb-4">
            <Sparkles className="w-3.5 h-3.5" />
            Free to start
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900">
            Pay only for what you generate
          </h2>
          <p className="mt-4 text-lg text-slate-600">
            Get &#8377;{SIGNUP_BONUS_AMOUNT} in your wallet on signup. Use it across any Bloom's level with no subscriptions, expiry, or hidden fees.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-6 mb-12">
          <div className="lg:col-span-1 bg-gradient-to-br from-teal-600 to-emerald-700 text-white rounded-2xl p-8 shadow-lg">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-white/15 mb-5">
              <Gift className="w-6 h-6" />
            </div>
            <div className="text-5xl font-bold">&#8377;{SIGNUP_BONUS_AMOUNT}</div>
            <div className="mt-1 text-teal-50 text-sm uppercase tracking-wider font-semibold">Wallet bonus on signup</div>
            <ul className="mt-6 space-y-3 text-sm text-teal-50">
              <li className="flex gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-white mt-1.5 flex-shrink-0" />
                Spend it on any item type: MCQ, MSQ, Fill-in, Matching, T/F
              </li>
              <li className="flex gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-white mt-1.5 flex-shrink-0" />
                Works across all Bloom's levels at the listed rates
              </li>
              <li className="flex gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-white mt-1.5 flex-shrink-0" />
                Enough to try a starter paper without adding payment details
              </li>
              <li className="flex gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-white mt-1.5 flex-shrink-0" />
                Generation history with one-click rerun
              </li>
            </ul>
            <Link
              to="/auth"
              className="mt-7 inline-flex items-center gap-2 px-5 py-2.5 bg-white text-teal-700 font-semibold rounded-lg hover:bg-teal-50 transition-colors"
            >
              Claim wallet bonus
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="lg:col-span-2 bg-white rounded-2xl p-8 shadow-sm border border-slate-200">
            <div className="flex items-center gap-2 text-slate-500 text-xs font-semibold uppercase tracking-wider mb-2">
              <Wallet className="w-4 h-4" />
              Pay-as-you-go rates
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-6">Per-question pricing by Bloom's taxonomy</h3>
            <div className="grid sm:grid-cols-2 gap-3">
              {orderedLevels.map((level) => (
                <div key={level} className="flex items-center justify-between border border-slate-200 rounded-xl px-4 py-3 hover:border-teal-300 hover:bg-teal-50/30 transition-all">
                  <div>
                    <div className="font-semibold text-slate-900">{level}</div>
                    <div className="text-xs text-slate-500">{bloomDescriptions[level]}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-teal-700">&#8377;{bloomPricing[level]}</div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-600">per question</div>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-5 rounded-xl border border-amber-200 bg-amber-50 p-4">
              <div className="text-sm font-semibold text-amber-900">
                Typical paper cost: &#8377;140-&#8377;240
              </div>
              <p className="mt-1 text-sm text-amber-800 leading-relaxed">
                A mixed 20-question NCERT paper usually lands in this range,
                depending on Bloom's levels. That makes the signup wallet a
                practical first-paper trial, not just a small coupon.
              </p>
            </div>
          </div>
        </div>

        <div className="grid sm:grid-cols-3 gap-4">
          <div className="bg-white border border-slate-200 rounded-xl p-5 flex gap-3">
            <div className="w-9 h-9 rounded-lg bg-teal-50 flex items-center justify-center flex-shrink-0">
              <Wallet className="w-5 h-5 text-teal-700" />
            </div>
            <div>
              <div className="font-semibold text-slate-900">Razorpay top-ups</div>
              <div className="text-sm text-slate-500">Add money instantly via UPI, cards, or netbanking.</div>
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-5 flex gap-3">
            <div className="w-9 h-9 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0">
              <RotateCcw className="w-5 h-5 text-emerald-700" />
            </div>
            <div>
              <div className="font-semibold text-slate-900">Auto refunds</div>
              <div className="text-sm text-slate-500">Failed generations are automatically refunded to your wallet.</div>
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-5 flex gap-3">
            <div className="w-9 h-9 rounded-lg bg-amber-50 flex items-center justify-center flex-shrink-0">
              <ShieldCheck className="w-5 h-5 text-amber-700" />
            </div>
            <div>
              <div className="font-semibold text-slate-900">No subscription</div>
              <div className="text-sm text-slate-500">Pay only when you generate. Balance never expires.</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
